"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recoverBitcoinAddress = void 0;
const bech32_1 = require("bech32");
const ethers_1 = require("ethers");
const bitcoin_varint_utils_1 = require("./bitcoin-varint-utils");
const types_1 = require("./types");
const bitcoinNetworkAddressData = {
    mainnet: {
        'p2pkh-uncompressed': {
            vOffset: 27,
            recoverFn: (compressedPublicKeyBytes) => {
                const publicKeyBytes = (0, ethers_1.getBytes)(ethers_1.SigningKey.computePublicKey(compressedPublicKeyBytes, false));
                return recoverP2PKHAddress(publicKeyBytes, '0x00');
            },
        },
        'p2pkh-compressed': {
            vOffset: 31,
            recoverFn: (compressedPublicKeyBytes) => recoverP2PKHAddress(compressedPublicKeyBytes, '0x00'),
        },
        'p2sh-p2wpkh': {
            vOffset: 35,
            recoverFn: (compressedPublicKeyBytes) => recoverP2SH_P2WPKHAddress(compressedPublicKeyBytes, '0x05'),
        },
        p2wpkh: {
            vOffset: 39,
            recoverFn: (compressedPublicKeyBytes) => recoverP2WPKHAddress(compressedPublicKeyBytes, 'bc'),
        },
    },
    testnet: {
        'p2pkh-uncompressed': {
            vOffset: 27,
            recoverFn: (compressedPublicKeyBytes) => {
                const publicKeyBytes = (0, ethers_1.getBytes)(ethers_1.SigningKey.computePublicKey(compressedPublicKeyBytes, false));
                return recoverP2PKHAddress(publicKeyBytes, '0x6F');
            },
        },
        'p2pkh-compressed': {
            vOffset: 31,
            recoverFn: (compressedPublicKeyBytes) => recoverP2PKHAddress(compressedPublicKeyBytes, '0x6F'),
        },
        'p2sh-p2wpkh': {
            vOffset: 35,
            recoverFn: (compressedPublicKeyBytes) => recoverP2SH_P2WPKHAddress(compressedPublicKeyBytes, '0xc4'),
        },
        p2wpkh: {
            vOffset: 39,
            recoverFn: (compressedPublicKeyBytes) => recoverP2WPKHAddress(compressedPublicKeyBytes, 'tb'),
        },
    },
};
function hashBitcoinMessage(message) {
    const messagePrefix = '\x18Bitcoin Signed Message:\n';
    const encodedMessageLength = (0, bitcoin_varint_utils_1.encodeBitcoinVarUint)(message.length);
    const messageEncodingLength = encodedMessageLength.length;
    const encodedMessage = (0, ethers_1.solidityPacked)(['string', 'uint' + (messageEncodingLength * 8).toString(10), 'bytes'], [messagePrefix, '0x' + encodedMessageLength.toString('hex'), message]);
    return (0, ethers_1.sha256)((0, ethers_1.sha256)(encodedMessage));
}
function recoverBitcoinAddress(message, signature, bitcoinNetwork) {
    const hash = hashBitcoinMessage((0, ethers_1.toUtf8Bytes)(message));
    const signatureBytes = (0, ethers_1.getBytes)(signature);
    // Reverse position of v, since Ethers expects it at the end and Bitcoin
    // wallets produce it at the beginning.
    const ethersSignatureBytes = (0, ethers_1.getBytes)((0, ethers_1.concat)([signatureBytes.slice(1), signatureBytes.slice(0, 1)]));
    const originalV = ethersSignatureBytes[64];
    const addressAdjustedV = // undo bip137 offset
     originalV >= 39 // p2wpkh
        ? originalV - 39
        : originalV >= 35 // p2sh-p2wpkh
            ? originalV - 35
            : originalV >= 31 // p2pkh compressed
                ? originalV - 31
                : originalV - 27; // p2pkh uncompressed
    ethersSignatureBytes[64] = addressAdjustedV;
    const addressTypeData = Object.values(bitcoinNetworkAddressData[bitcoinNetwork]).find(({ vOffset }) => originalV - vOffset >= 0 && originalV - vOffset < 4);
    if (addressTypeData === undefined) {
        throw new types_1.SignInWithWalletError(types_1.SignInWithWalletErrorType.INVALID_SIGNATURE, 'Recovered address is of unknown type.');
    }
    const publicKeyBytes = (0, ethers_1.getBytes)(ethers_1.SigningKey.recoverPublicKey(hash, ethers_1.Signature.from((0, ethers_1.hexlify)(ethersSignatureBytes))));
    const compressedPublicKeyBytes = (0, ethers_1.getBytes)(ethers_1.SigningKey.computePublicKey(publicKeyBytes, true));
    return addressTypeData.recoverFn(compressedPublicKeyBytes);
}
exports.recoverBitcoinAddress = recoverBitcoinAddress;
/**
 * HASH160 is a commonly-used combination of hashes in Bitcoin, ripemd160(sha256(data)).
 */
function hash160(data) {
    return (0, ethers_1.ripemd160)((0, ethers_1.sha256)(data));
}
/**
 * Base58Check encoding encodes data as base58, adds a prefixed version byte,
 * and then appends the first 4 bytes of the double-sha256 of the versioned
 * data as a checksum. It is used for pre-bech32 Bitcoin addresses.
 */
function base58CheckEncode(version, data) {
    const versionedData = (0, ethers_1.concat)([version, data]);
    // Checksum is the first 4 bytes of the double-sha256 of the versioned data.
    const checksum = (0, ethers_1.getBytes)((0, ethers_1.sha256)((0, ethers_1.sha256)(versionedData))).slice(0, 4);
    return (0, ethers_1.encodeBase58)((0, ethers_1.concat)([versionedData, checksum]));
}
function recoverP2PKHAddress(publicKey, version) {
    const pubKeyHash = hash160(publicKey);
    return base58CheckEncode(version, pubKeyHash);
}
function recoverP2SH_P2WPKHAddress(compressedPublicKeyBytes, version) {
    const pubKeyHash = hash160(compressedPublicKeyBytes);
    const scriptPubKey = hash160((0, ethers_1.solidityPacked)(['bytes2', 'bytes20'], ['0x0014', pubKeyHash]));
    return base58CheckEncode(version, scriptPubKey);
}
function recoverP2WPKHAddress(compressedPublicKeyBytes, prefix) {
    const pubKeyHash = hash160(compressedPublicKeyBytes);
    return bech32_1.bech32.encode(prefix, [0, ...bech32_1.bech32.toWords((0, ethers_1.getBytes)(pubKeyHash))]);
}
